import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AlertTriangle,
  Network,
  ShoppingCart,
  ShoppingBag,
} from "lucide-react";
import { FaExternalLinkAlt } from "react-icons/fa";
import VerifiedIcon from "@mui/icons-material/Verified";
import Tooltip from "@mui/material/Tooltip";
import PublicIcon from "@mui/icons-material/Public";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import { BUSINESS_FUNCTION_OPTIONS } from "../../../constants/Dashboard";
import AdlsIcon from "../../../assests/ADLS-SVG.svg";
import DatabricksSVG from "../../../assests/Databricks.svg";
import AzureDatabricksSVG from "../../../assests/Azure.svg";
import PowerBIIcon from "../../../assests/power-bi-icon.svg";
import LogoImage from "../../ProductLogo/LogoImage";
import KeyIcon from "@mui/icons-material/Key";
import ViewKpiModal from "./Viewkpi/components/ViewKpiModal";
import GcpIcon from "../../../assests/google-cloud.svg";
import {
  ADD_TO_CART,
  REMOVE_FROM_CART,
} from "../../../redux/domainAddToCart/domainAddToCart.type";

const ContractCard = ({
  contract,
  onReportSubscribe,
  onViewProduct,
  onViewAssetDetails,
  activeTab,
  maxHits,
  showCheckbox,
  selectedTab,
}) => {
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.domainAddToCart);
  const isAnalytics = contract.type === "analyticalProduct";
  const contractProductName = contract?.productName;

  const mapAssetToCartItem = (asset) => ({
    id: asset.id,
    name: asset.name,
    productName: asset.productName || "",
  });
  console.log("contract.name:", contract.name);

  const isSelected = (assetId) => cartItems.some((item) => item.id === assetId);
  const encodeWithPlus = (str) => {
    return encodeURIComponent(str || '').replace(/%20/g, '+');
  };

  // Add this new function to convert dot back to pipe for the URL
  const prepareAssetNameForUrl = (name) => {
    return name ? name.replace(/\./g, "|") : name;
  };
  const formatAssetName = (name) => {
    return name ? name.replace(/\|/g, ".") : name;
  };
  const renderWithTooltip = (text, charLimit = 20) => {
    if (!text) return <span>N/A</span>;

    const isLong = text.length > charLimit;
    const shortText = text.slice(0, charLimit);

    return (
      <Tooltip
        title={isLong ? text : ""}
        placement="top"
        arrow
        disableHoverListener={!isLong}
      >
        <span className="cursor-pointer">
          {isLong ? `${shortText}...` : text}
        </span>
      </Tooltip>
    );
  };

  const getDataLayerClass = (dataLayer) => {
    switch (dataLayer) {
      case "analyticalProduct":
        return "analytical-product";
      case "bronze":
        return "bronze";
      case "gold":
        return "gold";
      case "silver":
        return "silver";
      case "ingestion":
        return "ingestion";
      default:
        return "";
    }
  };

  const serviceNowTicketLink =
    "https://marsprod.service-now.com/sp?id=incident_helper";

  // Parse tags from API response
  const parseTags = (tags) => {
    if (!tags || tags === null || tags === undefined) {
      return null;
    }
    // Handle both string and array types
    const tagsString = typeof tags === "string" ? tags : String(tags);
    if (tagsString.trim() === "") {
      return null;
    }
    return tagsString
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
  };

  const checked =
    cartItems &&
    cartItems?.some((item) => item.id === contract?.id);

  const handleSelectAsset = (e) => {
    const { checked } = e.target;

    if (checked) {
      dispatch({
        type: ADD_TO_CART,
        payload: [mapAssetToCartItem(contract)],
      });
    } else {
      dispatch({
        type: REMOVE_FROM_CART,
        payload: [contract?.id],
      });
    }
  };

  const handleAddToCart = () => {
    if (isSelected(contract?.id)) {
      dispatch({
        type: REMOVE_FROM_CART,
        payload: [contract?.id],
      });
    } else {
      dispatch({
        type: ADD_TO_CART,
        payload: [mapAssetToCartItem(contract)],
      });
    }
  };

  const tags = parseTags(contract.tags);
  const [showAllTags, setShowAllTags] = useState(false);
  const [showMoreTags, setShowMoreTags] = useState(false);
  const [openTagsTooltip, setOpenTagsTooltip] = useState(false);
  const [showKpiModal, setShowKpiModal] = useState(false);

  // Color palette for tags - different colors for variety
  const tagColorPalette = [
    { bg: "#e0e7ff", color: "#4338ca", border: "#c7d2fe" }, // Indigo
    { bg: "#fce7f3", color: "#be185d", border: "#f9a8d4" }, // Pink
    { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" }, // Blue
    { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" }, // Yellow
    { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7" }, // Green
    { bg: "#e9d5ff", color: "#6b21a8", border: "#c084fc" }, // Purple
    { bg: "#fed7aa", color: "#9a3412", border: "#fdba74" }, // Orange
    { bg: "#fecaca", color: "#991b1b", border: "#fca5a5" }, // Red
    { bg: "#cffafe", color: "#164e63", border: "#67e8f9" }, // Cyan
    { bg: "#e5e7eb", color: "#374151", border: "#9ca3af" }, // Gray
  ];

  const getTagColor = (index) => {
    return tagColorPalette[index % tagColorPalette.length];
  };

  const MAX_VISIBLE_TAGS = 5;
  const visibleTags =
    tags && tags.length > 0
      ? showAllTags
        ? tags
        : tags.slice(0, MAX_VISIBLE_TAGS)
      : [];
  const remainingCount =
    tags && tags.length > MAX_VISIBLE_TAGS ? tags.length - MAX_VISIBLE_TAGS : 0;

  const isAdlsSource = contract.source_type?.toLowerCase() === "adls";
  const isGcp = contract.source_type?.toLowerCase() === "uc_gcp";
  const assetIcon = useMemo(() => {
    if (isAnalytics) {
      return (
        <div className="asset-icon-container">
          <img src={PowerBIIcon} alt="ADLS icon" className="table-icon-img" />
        </div>
      );
    }

    if (isAdlsSource) {
      return (
        <div className="asset-icon-container">
          <img src={AdlsIcon} alt="ADLS icon" className="table-icon-img" />
        </div>
      );
    }

    if (isGcp) {
      return (
        <div className="asset-icon-container">
          <img src={GcpIcon} alt="GCP icon" className="table-icon-img" />
        </div>
      );
    }

    return (
      <div className="asset-icon-container">
        <img
          src={AzureDatabricksSVG}
          alt="Azure Databricks"
          className="table-icon-dual-img"
        />
        <img
          src={DatabricksSVG}
          alt="Databricks"
          className="table-icon-dual-img"
        />
      </div>
    );
  }, [isAdlsSource]);
  const layer = getDataLayerClass(contract.dataLayer);
  const showProductLogo = layer === "bronze" || layer === "ingestion";
  return (
    <>
      <div key={contract.id} className="contract-card">
        <div className="card-header" style={{ backgroundColor: "#F1F6FB" }}>
          <div className="header-content">
            <div>
              <div className="card-title">
                {selectedTab === "assets" && showCheckbox && (
                  <input
                    type="checkbox"
                    id="select-all"
                    className="h-4 rounded border-gray-300 text-[#bb1ccc] focus:ring-[#bb1ccc]"
                    onChange={handleSelectAsset}
                    checked={checked}
                  />
                )}

                <div className="flex items-center">
                  <Tooltip title="Data Steward Certified" placement="top">
                    <VerifiedIcon
                      sx={{ marginRight: "5px", color: "#A5DB00" }}
                    />
                  </Tooltip>
                  <a
                    href={`/dataDomain?l1=${encodeWithPlus(contract.parentDomainName)}&l2=${encodeWithPlus(contract.domainName)}&l3=${encodeWithPlus(contract.subDomainName)}&l4=${encodeWithPlus(contract.productName)}&l5=${encodeWithPlus(prepareAssetNameForUrl(contract.name))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#000080] font-bold no-underline hover:underline hover:underline-offset-4 hover:decoration-blue-800"
                  >
                    <h3 style={{ color: "#bb1ccc", fontWeight: "bold" }}>
                      {formatAssetName(contract.name)}
                    </h3>
                  </a>
                </div>
                {assetIcon}
                {!isAnalytics ? (
                  contract.dataLayer && (
                    <span
                      className={`data-layer-tag ${layer} p-1 border-xl rounded`}
                    >
                      {contract?.dataLayer === "analyticalProduct"
                        ? "Analytical Product"
                        : contract.dataLayer.charAt(0).toUpperCase() +
                        contract.dataLayer.slice(1).toLowerCase()}
                    </span>
                  )
                ) : (
                  <span className="data-layer-tag analytical-product p-1 border-xl rounded">
                    Analytical Product{" "}
                  </span>
                )}

                <div
                  className="bg-yellow-100 p-1 border-xl text-yellow-700 rounded"
                  style={{ fontSize: "10px", marginLeft: "8px" }}
                >
                  Version {contract?.version}
                </div>
              </div>
              {!isAnalytics && (
                <div className="flex gap-[40px] ">
                  <div className="flex items-center ">
                    <span className="mr-5 text-[14px] font-semibold text-gray-700 whitespace-nowrap">
                      Tags
                    </span>

                    <div className="flex items-center gap-2 flex-nowrap">
                      {tags === null || tags.length === 0 ? (
                        <span className="text-gray-400 text-xs italic">
                          N/A
                        </span>
                      ) : (
                        <>
                          {tags.slice(0, 3).map((tag, index) => {
                            const tagColor = getTagColor(index);
                            return (
                              <span
                                key={index}
                                className="px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
                                style={{
                                  backgroundColor: tagColor.bg,
                                  color: tagColor.color,
                                  borderColor: tagColor.border,
                                }}
                              >
                                {tag}
                              </span>
                            );
                          })}

                          {tags.length > 3 && (
                            <Tooltip
                              title={tags.slice(3).join(", ")}
                              arrow
                              placement="top"
                            >
                              <span
                                className="px-3 py-1 rounded-full text-xs font-semibold border cursor-pointer
                         bg-blue-100 text-blue-800 hover:bg-blue-200 hover:border-blue-800"
                              >
                                +{tags.length - 3}
                              </span>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div
                    className="p-2  border-xl text-[#6b7280] "
                    style={{ fontSize: "10px", marginLeft: "8px" }}
                  >
                    Last Updated -{" "}
                    <span className="text-[#111827] font-bold">
                      {contract.lastUpdated}
                    </span>
                  </div>
                </div>
              )}

              {isAnalytics && (
                <div className="flex gap-[40px] mt-[13px]">
                  <div className="flex items-center ">
                    <span className="mr-5 text-[14px] font-semibold text-gray-700 whitespace-nowrap">
                      Tags
                    </span>

                    <div className="flex items-center gap-2 flex-nowrap">
                      {tags === null || tags.length === 0 ? (
                        <span className="text-gray-400 text-xs italic">
                          N/A
                        </span>
                      ) : (
                        <>
                          {tags.slice(0, 3).map((tag, index) => {
                            const tagColor = getTagColor(index);
                            return (
                              <span
                                key={index}
                                className="px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap"
                                style={{
                                  backgroundColor: tagColor.bg,
                                  color: tagColor.color,
                                  borderColor: tagColor.border,
                                }}
                              >
                                {tag}
                              </span>
                            );
                          })}

                          {tags.length > 3 && (
                            <Tooltip
                              title={tags.slice(3).join(", ")}
                              arrow
                              placement="top"
                            >
                              <span
                                className="px-3 py-1 rounded-full text-xs font-semibold border cursor-pointer
                 bg-blue-100 text-blue-800 hover:bg-blue-200 hover:border-blue-800"
                              >
                                +{tags.length - 3}
                              </span>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div
                    className="p-2 ml-[20px] border-xl text-[#6b7280] "
                    style={{ fontSize: "10px", marginLeft: "8px" }}
                  >
                    Last Updated -{" "}
                    <span className="text-[#111827] font-bold">
                      {contract.lastUpdated}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-center gap-4">
              <div className="quality-score">
                {showProductLogo && (
                  <div className="ml-3 mb-3 inline-flex items-end">
                    {/* <ProductLogo
                      productId={contract.productId}
                      className="h-12 w-auto object-contain"
                    /> */}
                    <div className="ml-1 inline-flex items-center gap-2">
                      {contract.logoIds?.map((l) => (
                        <LogoImage key={l} logoId={l} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {!isAnalytics && (
                <div className="quality-score">
                  <div className="score-label">Quality Score</div>
                  <div className="score-value">{contract.qualityScore}%</div>
                </div>
              )}

              {isAnalytics && (
                <div className="quality-score">
                  <div className="score-label">Overall Status</div>
                  <div className="score-value" style={{ fontSize: "14px" }}>
                    {contract.overall_status || "N/A"}
                  </div>
                </div>
              )}
            </div>
          </div>


        </div>

        <div className="card-body">
          {!isAnalytics && (
            <p className="card-description" style={{ fontSize: "13px" }}>
              {contract?.description &&
                contract.description.toLowerCase() !== "na"
                ? contract.description
                : "Description not available"}
            </p>
          )}
          {isAnalytics && (
            <p className="card-description" style={{ fontSize: "13px" }}>
              {contract?.report_objective && contract.report_objective !== "NA"
                ? contract.report_objective
                : "Description not available"}
            </p>
          )}

          {!isAnalytics && (
            <div className="flex gap-4 mt-4 mb-[10px]">
              <div className="flex justify-between items-center p-4 rounded-xl bg-white border border-gray-300 w-[190px]">
                <div>
                  <p className="text-[11px] text-gray-500">Markets Covered</p>
                  <p className="text-[14px] font-semibold">
                    {renderWithTooltip(contract.region, 10)}
                  </p>
                </div>
                <PublicIcon className="text-[#1CC47E]" sx={{ fontSize: 26 }} />
              </div>

              <div className="flex justify-between items-center p-4 rounded-xl bg-white border border-gray-300 w-[190px]">
                <div>
                  <p className="text-[11px] text-gray-500">Business Function</p>
                  <p className="text-[14px] font-semibold">
                    {renderWithTooltip(
                      BUSINESS_FUNCTION_OPTIONS.find(
                        (option) => option.value === contract.business_function
                      )?.label || contract.business_function
                    )}
                  </p>
                </div>
                <CorporateFareIcon
                  className="text-indigo-600 text-[22px]"
                  sx={{ fontSize: 26 }}
                />
              </div>
              {/* DQ Issues */}
              <div className="flex justify-between items-center p-4 rounded-xl bg-white border border-gray-300 w-[190px]">
                <div>
                  <p className="text-[11px] text-gray-500 mb-[2px]">
                    DQ Issues
                  </p>
                  <p className="text-[14px] font-semibold text-gray-900">
                    {contract.issues?.count || "NA"} rules failing
                  </p>
                </div>
                <AlertTriangle className="text-[#f59e0b]" />
              </div>


              <div className="flex justify-between items-center p-3 ounded-xl bg-white border border-gray-300 w-[210px]">
                <div>
                  <p className="text-[11px] text-gray-500 mb-[2px] whitespace-nowrap">Usage Trend</p>
                  <p className="text-[11px] font-semibold text-gray-900">

                    {contract.assetTrend != null && contract.assetTrend !== "N/A"

                      ? Number(contract.assetTrend).toLocaleString()

                      : "N/A"}{" "}

                    hits
                  </p>
                  <p className="text-[11px] font-semibold text-gray-900">last 6 months</p>
                </div>

                {(() => {

                  const hits =

                    contract.assetTrend != null && contract.assetTrend !== "N/A"

                      ? Number(contract.assetTrend)

                      : 0;

                  const pct = maxHits && maxHits > 0 ? (hits / maxHits) * 100 : 0;
                  console.log("DEBUG →", { assetTrend: contract.assetTrend, hits, maxHits, pct });


                  const getFilledBars = (pct) => {

                    if (pct <= 0) return 0;

                    if (pct <= 20) return 1;

                    if (pct <= 40) return 2;

                    if (pct <= 60) return 3;

                    if (pct <= 80) return 4;

                    return 5;

                  };

                  const filledBars = getFilledBars(pct);

                  const barHeights = [8, 12, 16, 20, 24];

                  return (
                    <Tooltip

                      title={
                        <div style={{ textAlign: "center", padding: "2px 4px" }}>
                          <div style={{ fontSize: "13px", fontWeight: "bold" }}>

                            {hits === 0 ? "No usage recorded" : `${pct.toFixed(2)}% percentile`}
                          </div>

                          {hits > 0 && (
                            <div style={{ fontSize: "11px", marginTop: "2px", color: "#93c5fd" }}>

                              {hits.toLocaleString()} / {maxHits?.toLocaleString()} hits
                            </div>

                          )}
                        </div>

                      }

                      placement="top"

                      arrow
                    >
                      <div className="flex items-end gap-[3px] h-7 cursor-pointer">

                        {barHeights.map((height, index) => {

                          const isFilled = index < filledBars;

                          return (
                            <div

                              key={index}

                              style={{ width: "5px", height: `${height}px`, borderRadius: "2px", transition: "background-color 0.2s ease" }}

                              className={

                                isFilled

                                  ? "bg-[#3b82f6] "

                                  : "bg-[#dbeafe]"

                              }

                            />

                          );

                        })}
                      </div>
                    </Tooltip>

                  );

                })()}
              </div>



              <div className="flex flex-col gap-1 p-4 rounded-xl bg-white border border-gray-300 w-[350px]">
                <p className="text-sm text-gray-600">
                  Product Owner :{" "}
                  <span className="font-semibold">
                    {renderWithTooltip(contract.productOwner || "N/A", 30)}
                  </span>
                </p>

                <p className="text-sm text-gray-600">
                  Data Steward :{" "}
                  <span className="font-semibold">
                    {" "}
                    {contract.technical_data_steward || "N/A"}{" "}
                  </span>
                </p>
                <p className="text-sm text-gray-600">
                  Asset Owner :{" "}
                  <span className="font-semibold">
                    {" "}
                    {contract.owner || "N/A"}{" "}
                  </span>
                </p>
              </div>
            </div>
          )}

          {isAnalytics && (
            <div className="flex gap-4 mt-4 mb-4">
              <div className="flex justify-between items-center p-4 rounded-xl bg-white border border-gray-300 w-[190px]">
                <div>
                  <p className="text-[11px] text-gray-500">Markets Covered</p>
                  <p className="text-[14px] font-semibold">
                    {renderWithTooltip(contract.region, 10)}
                  </p>
                </div>
                <PublicIcon className="text-[#1CC47E]" sx={{ fontSize: 26 }} />
              </div>

              <div className="flex justify-between items-center p-4 rounded-xl bg-white border border-gray-300 w-[190px]">
                <div>
                  <p className="text-[11px] text-gray-500">Domain</p>
                  <p className="text-[14px] font-semibold">
                    {renderWithTooltip(contract.domain_name, 10)}
                  </p>
                </div>
                <Network className="text-[#2B65EC] text-[20px]" />
              </div>

              <div className="flex justify-between items-center p-4 rounded-xl bg-white border border-gray-300 w-[190px]">
                <div>
                  <p className="text-[11px] text-gray-500">Business Function</p>
                  <p className="text-[14px] font-semibold">
                    {renderWithTooltip(
                      BUSINESS_FUNCTION_OPTIONS.find(
                        (option) => option.value === contract.business_function
                      )?.label || contract.business_function
                    )}
                  </p>
                </div>
                <CorporateFareIcon
                  className="text-indigo-600"
                  sx={{ fontSize: 26 }}
                />
              </div>

              <div className="flex justify-between items-center p-4 rounded-xl bg-white border border-gray-300 w-[190px]">
                <div>
                  <p className="text-[11px] text-gray-500">Report Type</p>
                  <p className="text-[14px] font-semibold">
                    {contract.report_type || "N/A"}
                  </p>
                </div>
                <AnalyticsIcon
                  className="text-blue-600"
                  sx={{ fontSize: 30 }}
                />
              </div>

              <div className="flex items-center justify-center ">
                <div className="flex flex-col gap-1 p-4 rounded-xl bg-white border border-gray-300 w-[350px]">
                  <p className="text-sm text-gray-600">
                    Product Owner :{" "}
                    <span className="font-semibold">
                      {renderWithTooltip(contract.productOwner || "N/A", 20)}
                    </span>
                  </p>

                  <p className="text-sm text-gray-600">
                    Data Steward :{" "}
                    <span className="font-semibold">
                      {" "}
                      {contract.technical_data_steward || "N/A"}{" "}
                    </span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Report Owner :{" "}
                    <span className="font-semibold">
                      {" "}
                      {contract.report_owner || "N/A"}{" "}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="card-actions">
            <div className="action-links">
              <button
                className="link-button bg-blue-100 text-blue-700 border border-blue-400"
                onClick={onViewProduct}
              >
                View Product
              </button>
              <button
                className={`link-button bg-green-100 text-green-700 border border-green-400 ${isAnalytics ? "opacity-50 !cursor-not-allowed" : ""
                  }`}
                onClick={!isAnalytics ? onViewAssetDetails : undefined}
                disabled={isAnalytics}
              >
                View Asset Details
              </button>

              {isAnalytics && (
                <button
                  disabled
                  className="link-button text-purple-700 border border-purple-400 bg-purple-100 opacity-50 !cursor-not-allowed"
                >
                  View Sections
                </button>
              )}
              {isAnalytics && (
                <button
                  disabled
                  className="link-button bg-orange-100 text-orange-700 border border-orange-400 opacity-50 !cursor-not-allowed"
                >
                  View Lineage
                </button>
              )}

              {isAnalytics && (
                <button
                  disabled
                  className="link-button link-button bg-orange-100 text-orange-700 border border-orange-400 opacity-50 !cursor-not-allowed"
                >
                  View Schematic Model
                </button>
              )}
              {isAnalytics && (
                <button
                  // disabled
                  onClick={() => setShowKpiModal(true)}
                  className="
      link-button link-button bg-indigo-100 text-indigo-700 border border-indigo-400 "
                >
                  View KPI
                </button>
              )}



            </div>
            {activeTab === "all" && (
              <div className="flex gap-2 items-center">
                <a
                  href={serviceNowTicketLink}
                  target="_blank"
                  className="px-3 py-1 rounded bg-[#1D7285] text-white  whitespace-nowrap flex text-center items-center h-[37px]"
                >
                  <FaExternalLinkAlt className="icon" />
                  <span className="ml-[8px] text-[12px]">Raise Incident</span>
                </a>

                {selectedTab === "assets" && showCheckbox ? (
                  <button
                    className="px-3 py-1 rounded bg-[#1D7285] text-white whitespace-nowrap flex justify-center items-center h-[37px] min-w-[130px]"
                    onClick={handleAddToCart}
                  >
                    {!checked ? (
                      <ShoppingCart className="h-5" />
                    ) : (
                      <ShoppingBag className="h-5" />
                    )}
                    <span className="ml-[8px] text-[12px]">
                      {" "}
                      {!checked ? "Add to Cart" : "Added"}
                    </span>
                  </button>
                ) : (
                  <button
                    onClick={onReportSubscribe}
                    className="px-3 py-1 rounded bg-[#1D7285] text-white whitespace-nowrap flex text-center items-center h-[37px]"
                  >
                    <KeyIcon className="icon" />
                    <span className="ml-[8px] text-[12px]">Request Access</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {showKpiModal && (
        <ViewKpiModal
          name={contractProductName}
          showKpiModal={showKpiModal}
          setShowKpiModal={setShowKpiModal}
        />
      )}
    </>
  );
};

export default ContractCard;
